import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileImage,
  FilePlus2,
  Loader2,
  Thermometer,
  Waypoints,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getLocations } from '@/features/locations/api';
import { getTicketsByCustomer } from '@/features/quotes/api';
import { getAccount } from '@/features/accounts/api';
import type { Location } from '@/features/locations/types';
import { legacyRoutes } from '@/lib/legacy';
import { getChecklistFileUrl } from '../api';
import {
  createInstallationQualificationFromJob,
  fetchInstallationIqContext,
  formatTransmitterDeviceModel,
  getInstallationIqDisplay,
  getInstallationIqHardwareId,
  type InstallationIqContext,
  type LegacyIqFormValues,
  type LegacyIqRecord,
} from '../iq-api';
import { useAccountQuotes, useMarkInstallationIqCreated } from '../hooks/useInstallationJobs';
import type { InstallationJob } from '../types';

interface InstallationIqSectionProps {
  job: InstallationJob;
  linkedQuoteNumber?: string | null;
}

interface IqEvidence {
  fileId: string;
  label: string;
}

interface InstalledSensorCandidate {
  assignment: InstallationJob['sensors'][number];
  context: InstallationIqContext | null;
  evidence: IqEvidence[];
  isLoading: boolean;
  isError: boolean;
}

const EMPTY_VALUE = '__none__';
const BOOLEAN_WITH_NA_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'NA', label: 'N/A' },
] as const;
const POWER_SOURCE_OPTIONS = [
  { value: 'emergency', label: 'Emergency Power' },
  { value: 'regular', label: 'Regular Power' },
  { value: '208V', label: '208V Adapter' },
  { value: 'poe', label: 'Power over Ethernet' },
] as const;
const TRANSMITTER_POSITION_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'front', label: 'Front' },
  { value: 'back', label: 'Back' },
  { value: 'inside', label: 'Inside' },
  { value: 'wall', label: 'Wall' },
  { value: 'other', label: 'Other' },
] as const;
const PROBE_POSITION_OPTIONS = [
  { value: 'NA', label: 'N/A' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'shelf', label: 'Shelf' },
  { value: 'display', label: 'Display' },
  { value: 'other', label: 'Other' },
] as const;
const ENTRY_POINT_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'front', label: 'Front' },
  { value: 'back', label: 'Back' },
  { value: 'NA', label: 'N/A' },
] as const;
const ANCHOR_OPTIONS = [
  { value: 'magnet', label: 'Magnet' },
  { value: 'velcro', label: 'Velcro' },
  { value: 'tiewrap', label: 'Tie Wrap' },
] as const;
const BOTTLE_OPTIONS = [
  { value: 'glycol 5mL', label: 'Glycol 5mL' },
  { value: 'air', label: 'Air' },
  { value: 'bottle', label: 'Equipment Bottle' },
  { value: 'no thermobuffer', label: 'No Thermobuffer' },
] as const;
const DISPLAY_OPTIONS = [
  { value: '0x00', label: 'No screen' },
  { value: '0x01', label: 'LCD V1' },
  { value: '0x02', label: 'LCD V2' },
  { value: '0x03', label: 'LCD V3' },
] as const;
const DRAGINO_FIRMWARE_DISTRIBUTION_IDS = [
  '6414c0a435befe0793c93553',
  '6414c0b335befe0793c93561',
] as const;
// Template keys drift in production (empty, "ATEK_label_affixed", truncated slugs),
// so misc answers are matched like evidence photos: normalized key or label.
const IQ_MISC_CHECKLIST_REQUIREMENTS = {
  wiresSecured: {
    keys: ['probe_power_supply_ok', 'probe_power_wire_secured'],
    matches: (value: string) => (
      value.includes('sonde attachee')
      || (
        (value.includes('wiring') || value.includes('wire') || value.includes('fils') || value.includes('cable'))
        && (value.includes('secur') || value.includes('attach') || value.includes('cable tie'))
      )
    ),
  },
  logoPlaced: {
    keys: ['atek_label_affixed'],
    matches: (value: string) => (
      value.includes('atek')
      && (value.includes('label') || value.includes('logo') || value.includes('etiquette'))
    ),
  },
} as const;

const IQ_EVIDENCE_REQUIREMENTS = [
  {
    keys: ['installation_photo', 'completed_installation_photo', 'equipment_environment_photo'],
    matches: (value: string) => (
      value.includes('completed installation')
      || value.includes('installation terminee')
      || (value.includes('equipment') && value.includes('environment'))
      || (value.includes('equipement') && value.includes('environnement'))
    ),
  },
  {
    keys: ['probe_photo', 'probe_placement_photo', 'probe_inside_photo'],
    matches: (value: string) => (
      (value.includes('probe') && (value.includes('placement') || value.includes('inside')))
      || (value.includes('sonde') && (value.includes('placement') || value.includes('interieur')))
    ),
  },
  {
    keys: ['equipment_label_photo', 'unit_label_photo', 'refrigerator_id_photo', 'manufacturer_label_photo'],
    matches: (value: string) => (
      !value.includes('transmitter')
      && !value.includes('transmetteur')
      && (
        (
          (value.includes('label') || value.includes('etiquette'))
          && (
            value.includes('manufacturer')
            || value.includes('fabricant')
            || value.includes('equipment')
            || value.includes('equipement')
            || value.includes('refrigerator')
            || value.includes('refrigerateur')
          )
        )
        || value.includes('refrigerator id')
        || value.includes('identifiant du refrigerateur')
      )
    ),
  },
] as const;

function normalizeEvidenceText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function collectEvidence(
  job: InstallationJob,
  sensorMongoId: string,
): IqEvidence[] {
  const sensor = job.sensors.find((entry) => entry.mongoSensorId === sensorMongoId);
  const photoItems = (sensor?.checklist || []).filter((item) => item.photoFileId);

  return IQ_EVIDENCE_REQUIREMENTS
    .map((requirement) => photoItems.find((item) => {
      const key = normalizeEvidenceText(item.key).replace(/ /g, '_');
      const label = normalizeEvidenceText(item.displayName || '');
      return requirement.keys.some((candidate) => candidate === key)
        || requirement.matches(label);
    }))
    .filter((item): item is NonNullable<typeof item> => Boolean(item?.photoFileId))
    .map((item) => ({
      fileId: item.photoFileId!,
      label: item.displayName || `${sensor?.sensorName || 'Sensor'} checklist photo`,
    }));
}

function formatReading(candidate: InstalledSensorCandidate): string {
  const value = candidate.context?.sensor.currentValue;
  if (value === null || value === undefined || value === '') return 'No reading';

  const unit = candidate.context?.sensor.sensorTypeUnit ? ` ${candidate.context.sensor.sensorTypeUnit}` : '';
  return `${value}${unit}`;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return 'No timestamp';
  return new Date(value).toLocaleString('en-CA');
}

function getCheckedChecklistValue(
  assignment: InstallationJob['sensors'][number],
  requirement: { keys: readonly string[]; matches: (value: string) => boolean },
): string {
  const item = assignment.checklist?.find((entry) => {
    if (entry.type !== 'checkbox') return false;
    const key = normalizeEvidenceText(entry.key || '').replace(/ /g, '_');
    const label = normalizeEvidenceText(entry.displayName || '');
    return requirement.keys.some((candidate) => candidate === key)
      || requirement.matches(label);
  });
  return item?.value === true ? 'true' : '';
}

function getTechnicianName(candidate: InstalledSensorCandidate): string {
  const submittedBy = candidate.assignment.iqDraft?.submittedBy?.trim();
  if (submittedBy) return submittedBy;

  return candidate.assignment.checklist
    ?.find((item) => item.completedByName?.trim())
    ?.completedByName
    ?.trim() ?? '';
}

function getIqValidationError(values: LegacyIqFormValues): string | null {
  const application = values.application?.trim();
  const isAmbient = application?.toLowerCase() === 'ambient';

  if (!application) return 'IQ requires an application';
  if (!values.quoteNumber?.trim() && !values.ticketNumber?.trim()) {
    return 'IQ requires at least a quote or ticket number';
  }
  if (application.toLowerCase() === 'other' && !values.equipmentDescription?.trim()) {
    return 'IQ requires an equipment description when application is Other';
  }
  if (!isAmbient && (!values.manufacturer?.trim() || !values.model?.trim() || !values.serialNumber?.trim())) {
    return 'IQ requires manufacturer, model, and serial number for non-ambient equipment';
  }
  if (!values.transmitterDisplay?.trim()) return 'IQ requires the screen selection';
  if (values.transmitterPowerExternal === undefined || values.transmitterPowerExternal === null) {
    return 'IQ requires the external power selection';
  }
  if (values.transmitterPowerExternal && !values.transmitterPowerSource?.trim()) {
    return 'IQ requires a power source when the transmitter has external power';
  }
  if (!values.transmitterExtension?.trim()) return 'IQ requires the power extension wire answer';
  if (!values.transmitterSplitter?.trim()) return 'IQ requires the power splitter answer';
  if (!values.transmitterPosition?.trim()) return 'IQ requires the transmitter placement';
  if (!values.transmitterAnchor?.trim()) return 'IQ requires the transmitter anchor';
  if (!values.probeType?.trim()) return 'IQ requires the probe type';
  if (!values.probeBottle?.trim()) return 'IQ requires the thermobuffer/bottle selection';
  if (!values.probePosition?.trim()) return 'IQ requires the probe position';
  if (values.probePosition === 'other' && !values.probePositionOther?.trim()) {
    return 'IQ requires a custom probe position description';
  }
  if (!values.probeEntryPoint?.trim()) return 'IQ requires the probe entry point';
  if (!values.probeExtension?.trim()) return 'IQ requires the probe extension wire answer';
  return null;
}

function getAutoFillValidationError(candidate: InstalledSensorCandidate): string | null {
  const transmitter = candidate.context?.transmitter;
  if (!transmitter) return 'IQ requires a linked transmitter';

  const moduleId = getInstallationIqHardwareId(transmitter, 'module');
  const baseId = getInstallationIqHardwareId(transmitter, 'base');
  const connectivityId = getInstallationIqHardwareId(transmitter, 'connectivity');
  const isDragino = transmitter.mongoFirmwareDistributionId
    ? DRAGINO_FIRMWARE_DISTRIBUTION_IDS.includes(transmitter.mongoFirmwareDistributionId as typeof DRAGINO_FIRMWARE_DISTRIBUTION_IDS[number])
    : false;

  if (!moduleId) return 'IQ requires a transmitter module, but none is available for this transmitter';
  if (!isDragino && !baseId) return 'IQ requires a transmitter base, but none is available for this transmitter';
  if (!isDragino && !connectivityId) return 'IQ requires transmitter connectivity, but none is available for this transmitter';
  return null;
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value || EMPTY_VALUE} onValueChange={(next) => onChange(next === EMPTY_VALUE ? '' : next)}>
        <SelectTrigger>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={EMPTY_VALUE}>Select…</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function buildInitialFormValues(
  candidate: InstalledSensorCandidate,
  linkedQuoteNumber?: string | null,
): LegacyIqFormValues {
  const sensor = candidate.context?.sensor;
  const transmitter = candidate.context?.transmitter;
  const technicianDraft = candidate.assignment.iqDraft;

  return {
    quoteNumber: technicianDraft?.quoteNumber ?? linkedQuoteNumber ?? transmitter?.quoteNumber ?? sensor?.quotes?.[0] ?? '',
    ticketNumber: technicianDraft?.ticketNumber ?? transmitter?.ticketNumber ?? '',
    application: technicianDraft?.application ?? sensor?.equipmentType ?? '',
    equipmentDescription: technicianDraft?.equipmentDescription ?? '',
    locationMongoId: technicianDraft?.locationMongoId ?? sensor?.location?.mongoLocationId ?? '',
    locationName: technicianDraft?.locationName ?? sensor?.location?.name ?? '',
    siteMongoId: technicianDraft?.siteMongoId ?? sensor?.site?.mongoSiteId ?? '',
    siteName: technicianDraft?.siteName ?? sensor?.site?.name ?? '',
    manufacturer: technicianDraft?.manufacturer ?? sensor?.manufacturer ?? '',
    model: technicianDraft?.model ?? sensor?.model ?? '',
    serialNumber: technicianDraft?.serialNumber ?? sensor?.serialNumber ?? '',
    transmitterDisplay: technicianDraft?.transmitterDisplay
      ?? (transmitter ? getInstallationIqDisplay(transmitter) : null)
      ?? '',
    transmitterPowerExternal: technicianDraft?.transmitterPowerExternal,
    transmitterPowerSource: technicianDraft?.transmitterPowerSource ?? '',
    transmitterExtension: technicianDraft?.transmitterExtension ?? '',
    transmitterSplitter: technicianDraft?.transmitterSplitter ?? '',
    transmitterPosition: technicianDraft?.transmitterPosition ?? '',
    transmitterAnchor: technicianDraft?.transmitterAnchor ?? '',
    probeType: technicianDraft?.probeType ?? '',
    probeBottle: technicianDraft?.probeBottle ?? '',
    probePosition: technicianDraft?.probePosition ?? '',
    probePositionOther: technicianDraft?.probePositionOther ?? '',
    probeEntryPoint: technicianDraft?.probeEntryPoint ?? '',
    probeExtension: technicianDraft?.probeExtension ?? '',
    miscWiresSecured: technicianDraft?.miscWiresSecured
      || getCheckedChecklistValue(candidate.assignment, IQ_MISC_CHECKLIST_REQUIREMENTS.wiresSecured),
    miscLogoPlaced: technicianDraft?.miscLogoPlaced
      || getCheckedChecklistValue(candidate.assignment, IQ_MISC_CHECKLIST_REQUIREMENTS.logoPlaced),
  };
}

function IqCreationDialog({
  job,
  candidate,
  linkedQuoteNumber,
  open,
  onOpenChange,
  onCreated,
}: {
  job: InstallationJob;
  candidate: InstalledSensorCandidate | null;
  linkedQuoteNumber?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (sensorMongoId: string, iq: LegacyIqRecord) => Promise<void>;
}) {
  const [formValues, setFormValues] = useState<LegacyIqFormValues | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation('common');

  const accountId = candidate?.context?.sensor.account?.id ?? job.accountId ?? null;
  const selectedLocationId = formValues?.locationMongoId || '';

  const { data: account, isLoading: isAccountLoading } = useQuery({
    queryKey: ['installation-iq-account', accountId],
    queryFn: () => getAccount(accountId!),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
  const organisationId = candidate?.context?.sensor.mongoOrganisationId
    ?? account?.mongo_organisation_id
    ?? null;
  const isOrganisationLookupPending = !candidate?.context?.sensor.mongoOrganisationId
    && !!accountId
    && isAccountLoading;
  const { data: quotesResponse } = useAccountQuotes(accountId);
  const { data: tickets = [] } = useQuery({
    queryKey: ['installation-iq-tickets', organisationId],
    queryFn: () => getTicketsByCustomer(organisationId!),
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
  });
  const { data: locationsResponse } = useQuery({
    queryKey: ['installation-iq-locations', accountId, job.siteId],
    queryFn: () => getLocations({
      accountId: accountId ?? undefined,
      siteId: job.siteId ?? undefined,
      pageLength: 100,
      sortKey: 'name',
      sortDirection: 1,
    }),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
  const evidencePreviewQueries = useQueries({
    queries: (candidate?.evidence ?? []).map((item) => ({
      queryKey: ['installation-iq-evidence-preview', job.id, item.fileId],
      queryFn: () => getChecklistFileUrl(job.id, item.fileId),
      enabled: open,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const locations = locationsResponse?.locations ?? [];
  const quotes = quotesResponse?.quotes ?? [];
  const selectedQuoteNumber = formValues?.quoteNumber?.trim() ?? '';
  const selectedQuoteMissingFromPage = selectedQuoteNumber
    && !quotes.some((quote) => quote.quoteNumber === selectedQuoteNumber);
  const evidencePreviews = (candidate?.evidence ?? []).map((item, index) => ({
    ...item,
    url: evidencePreviewQueries[index]?.data ?? null,
  }));

  useEffect(() => {
    if (!open) {
      setFormValues(null);
      return;
    }
    if (!candidate) return;
    setFormValues((current) => current ?? buildInitialFormValues(candidate, linkedQuoteNumber));
  }, [candidate, linkedQuoteNumber, open]);

  const handleLocationChange = (value: string) => {
    if (!formValues) return;
    if (value === EMPTY_VALUE) {
      setFormValues({
        ...formValues,
        locationMongoId: '',
        locationName: '',
      });
      return;
    }

    const selected = locations.find((location) => location.mongoLocationId === value);
    setFormValues({
      ...formValues,
      locationMongoId: value,
      locationName: selected?.name || '',
      siteMongoId: selected?.mongoSiteId || formValues.siteMongoId,
      siteName: selected?.siteName || formValues.siteName,
    });
  };

  const handleSubmit = async () => {
    if (!candidate?.context?.sensor || !candidate.context.transmitter || !formValues) {
      toast.error('Missing IQ source data');
      return;
    }
    if (!organisationId) {
      toast.error('The IQ requires a customer account linked to a legacy organisation');
      return;
    }

    const autoFillValidationError = getAutoFillValidationError(candidate);
    if (autoFillValidationError) {
      toast.error(autoFillValidationError);
      return;
    }

    const validationError = getIqValidationError(formValues);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await createInstallationQualificationFromJob({
        jobId: job.id,
        organisationId,
        organisationName: candidate.context.sensor.account?.name
          ?? account?.name
          ?? job.accountName
          ?? 'Unknown',
        responsibleName: getTechnicianName(candidate),
        sensor: candidate.context.sensor,
        transmitter: candidate.context.transmitter,
        formValues,
        evidence: candidate.evidence,
      });
      await onCreated(candidate.assignment.mongoSensorId, created);
      toast.success(`IQ ${created.report_number} created`);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create IQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedLocation = locations.find((location) => location.mongoLocationId === selectedLocationId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create IQ from Approved Installation</DialogTitle>
          <DialogDescription>
            The job context, linked quote, live sensor/transmitter data, and technician checklist photos are preloaded here.
          </DialogDescription>
        </DialogHeader>

        {!candidate?.context ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {!candidate.context.transmitter && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Missing transmitter link</AlertTitle>
                <AlertDescription>
                  This sensor does not currently resolve to linked transmitter data, so the IQ cannot be created yet.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Sensor Auto-Fill</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Sensor</span>
                    <span className="font-medium text-right">{candidate.context.sensor.name}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Reading</span>
                    <span className="font-medium text-right">{formatReading(candidate)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Last reading</span>
                    <span className="font-medium text-right">{formatTimestamp(candidate.context.sensor.lastReadingAt)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Probe serial</span>
                    <span className="font-medium text-right">{candidate.context.sensor.probeSerial || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Calibration cert</span>
                    <span className="font-medium text-right">{candidate.context.sensor.calibrationCertNumber || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Calibration date</span>
                    <span className="font-medium text-right">{candidate.context.sensor.calibrationDate ? new Date(candidate.context.sensor.calibrationDate).toLocaleDateString('en-CA') : '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Location</span>
                    <span className="font-medium text-right">{candidate.context.sensor.location?.name || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Transmitter Auto-Fill</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Transmitter</span>
                    <span className="font-medium text-right">
                      {candidate.context.transmitter?.name || candidate.context.transmitter?.devId || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">{candidate.context.transmitter?.deviceType ? t('iqDevEui', 'DevEUI') : 'DEV ID'}</span>
                    <span className="font-medium text-right">{candidate.context.transmitter?.devId || '-'}</span>
                  </div>
                  {candidate.context.transmitter?.deviceType && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">{t('iqTransmitterModel', 'Transmitter model')}</span>
                      <span className="font-medium text-right">{formatTransmitterDeviceModel(candidate.context.transmitter.deviceType)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Firmware</span>
                    <span className="font-medium text-right">{candidate.context.transmitter?.currentFirmwareName || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Battery</span>
                    <span className="font-medium text-right">
                      {candidate.context.transmitter?.batteryVoltage ?? '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">RSSI</span>
                    <span className="font-medium text-right">{candidate.context.transmitter?.rssi ?? '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Ticket</span>
                    <span className="font-medium text-right">{candidate.context.transmitter?.ticketNumber || '-'}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Editable IQ Fields</CardTitle>
                <CardDescription>
                  Fields below are prefilled from the approved job and available device data. Only override them if needed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="iq-quote">Quote</Label>
                    <Select
                      value={formValues?.quoteNumber || EMPTY_VALUE}
                      onValueChange={(value) => setFormValues((current) => current ? {
                        ...current,
                        quoteNumber: value === EMPTY_VALUE ? '' : value,
                      } : current)}
                    >
                      <SelectTrigger id="iq-quote">
                        <SelectValue placeholder="No quote" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={EMPTY_VALUE}>No quote</SelectItem>
                        {selectedQuoteMissingFromPage && (
                          <SelectItem value={selectedQuoteNumber}>{selectedQuoteNumber}</SelectItem>
                        )}
                        {quotes.map((quote) => (
                          <SelectItem key={quote.id} value={quote.quoteNumber}>
                            {quote.quoteNumber} {quote.projectName ? `- ${quote.projectName}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="iq-ticket">Ticket</Label>
                    <Select
                      value={formValues?.ticketNumber || EMPTY_VALUE}
                      onValueChange={(value) => setFormValues((current) => current ? {
                        ...current,
                        ticketNumber: value === EMPTY_VALUE ? '' : value,
                      } : current)}
                    >
                      <SelectTrigger id="iq-ticket">
                        <SelectValue placeholder="No ticket" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={EMPTY_VALUE}>No ticket</SelectItem>
                        {tickets.map((ticket) => (
                          <SelectItem
                            key={ticket._id}
                            value={ticket.ticket_number ? String(ticket.ticket_number) : ticket._id}
                          >
                            {ticket.ticket_number ? `${ticket.ticket_number} - ` : ''}
                            {ticket.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="iq-location">Location</Label>
                    <Select
                      value={selectedLocationId || EMPTY_VALUE}
                      onValueChange={handleLocationChange}
                    >
                      <SelectTrigger id="iq-location">
                        <SelectValue placeholder="No location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={EMPTY_VALUE}>No location</SelectItem>
                        {locations.map((location: Location) => (
                          <SelectItem
                            key={location.id}
                            value={location.mongoLocationId || location.id}
                          >
                            {location.name}
                            {location.siteName ? ` - ${location.siteName}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedLocation?.siteName && (
                      <p className="text-xs text-muted-foreground">Site: {selectedLocation.siteName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="iq-application">Application</Label>
                    <Input
                      id="iq-application"
                      value={formValues?.application || ''}
                      onChange={(event) => setFormValues((current) => current ? {
                        ...current,
                        application: event.target.value,
                      } : current)}
                      placeholder="Freezer, fridge, incubator..."
                    />
                  </div>

                  {formValues?.application?.toLowerCase() === 'other' && (
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="iq-equipment-description">Equipment Description</Label>
                      <Input
                        id="iq-equipment-description"
                        value={formValues?.equipmentDescription || ''}
                        onChange={(event) => setFormValues((current) => current ? {
                          ...current,
                          equipmentDescription: event.target.value,
                        } : current)}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="iq-manufacturer">Manufacturer</Label>
                    <Input
                      id="iq-manufacturer"
                      value={formValues?.manufacturer || ''}
                      onChange={(event) => setFormValues((current) => current ? {
                        ...current,
                        manufacturer: event.target.value,
                      } : current)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="iq-model">Model</Label>
                    <Input
                      id="iq-model"
                      value={formValues?.model || ''}
                      onChange={(event) => setFormValues((current) => current ? {
                        ...current,
                        model: event.target.value,
                      } : current)}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="iq-serial">Serial Number</Label>
                    <Input
                      id="iq-serial"
                      value={formValues?.serialNumber || ''}
                      onChange={(event) => setFormValues((current) => current ? {
                        ...current,
                        serialNumber: event.target.value,
                      } : current)}
                    />
                  </div>

                  <SelectField
                    label="Screen (LCD)"
                    value={formValues?.transmitterDisplay || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      transmitterDisplay: value,
                    } : current)}
                    options={DISPLAY_OPTIONS}
                  />

                  <SelectField
                    label="External Power"
                    value={
                      formValues?.transmitterPowerExternal === true
                        ? 'true'
                        : formValues?.transmitterPowerExternal === false
                          ? 'false'
                          : ''
                    }
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      transmitterPowerExternal: value === 'true' ? true : value === 'false' ? false : undefined,
                    } : current)}
                    options={[
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' },
                    ]}
                  />

                  {formValues?.transmitterPowerExternal && (
                    <SelectField
                      label="Power Source"
                      value={formValues?.transmitterPowerSource || ''}
                      onChange={(value) => setFormValues((current) => current ? {
                        ...current,
                        transmitterPowerSource: value,
                      } : current)}
                      options={POWER_SOURCE_OPTIONS}
                    />
                  )}

                  <SelectField
                    label="Used Power Extension Wire"
                    value={formValues?.transmitterExtension || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      transmitterExtension: value,
                    } : current)}
                    options={BOOLEAN_WITH_NA_OPTIONS}
                  />

                  <SelectField
                    label="Used Power Splitter"
                    value={formValues?.transmitterSplitter || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      transmitterSplitter: value,
                    } : current)}
                    options={BOOLEAN_WITH_NA_OPTIONS}
                  />

                  <SelectField
                    label="Transmitter Placement"
                    value={formValues?.transmitterPosition || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      transmitterPosition: value,
                    } : current)}
                    options={TRANSMITTER_POSITION_OPTIONS}
                  />

                  <SelectField
                    label="Transmitter Anchor"
                    value={formValues?.transmitterAnchor || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      transmitterAnchor: value,
                    } : current)}
                    options={ANCHOR_OPTIONS}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="iq-probe-type">Probe Type</Label>
                    <Input
                      id="iq-probe-type"
                      value={formValues?.probeType || ''}
                      onChange={(event) => setFormValues((current) => current ? {
                        ...current,
                        probeType: event.target.value,
                      } : current)}
                    />
                  </div>

                  <SelectField
                    label="Thermobuffer / Bottle"
                    value={formValues?.probeBottle || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      probeBottle: value,
                    } : current)}
                    options={BOTTLE_OPTIONS}
                  />

                  <SelectField
                    label="Probe Position"
                    value={formValues?.probePosition || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      probePosition: value,
                    } : current)}
                    options={PROBE_POSITION_OPTIONS}
                  />

                  {formValues?.probePosition === 'other' && (
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="iq-probe-position-other">Probe Position Details</Label>
                      <Input
                        id="iq-probe-position-other"
                        value={formValues?.probePositionOther || ''}
                        onChange={(event) => setFormValues((current) => current ? {
                          ...current,
                          probePositionOther: event.target.value,
                        } : current)}
                      />
                    </div>
                  )}

                  <SelectField
                    label="Probe Entry Point"
                    value={formValues?.probeEntryPoint || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      probeEntryPoint: value,
                    } : current)}
                    options={ENTRY_POINT_OPTIONS}
                  />

                  <SelectField
                    label="Used Probe Extension Wire"
                    value={formValues?.probeExtension || ''}
                    onChange={(value) => setFormValues((current) => current ? {
                      ...current,
                      probeExtension: value,
                    } : current)}
                    options={BOOLEAN_WITH_NA_OPTIONS}
                  />

                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Technician Evidence</CardTitle>
                <CardDescription>
                  These checklist photos will be imported into the IQ automatically on save.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {evidencePreviews.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No technician checklist photos were found on this job for auto-import.</p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {evidencePreviews.map((evidence) => (
                      <div key={evidence.fileId} className="rounded-lg border p-3 space-y-2">
                        <div className="aspect-video overflow-hidden rounded border bg-muted">
                          {evidence.url ? (
                            <img
                              src={evidence.url}
                              alt={evidence.label}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                          )}
                        </div>
                        <p className="text-sm font-medium">{evidence.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isOrganisationLookupPending || !candidate?.context?.transmitter}
          >
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-2 h-4 w-4" />}
            Create IQ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InstallationIqSection({ job, linkedQuoteNumber }: InstallationIqSectionProps) {
  const [activeSensorId, setActiveSensorId] = useState<string | null>(null);
  const [activeContext, setActiveContext] = useState<{
    sensorId: string;
    context: InstallationIqContext;
  } | null>(null);
  const [refreshingSensorId, setRefreshingSensorId] = useState<string | null>(null);
  const [sessionCreatedIqBySensorId, setSessionCreatedIqBySensorId] = useState<Record<string, LegacyIqRecord>>({});
  const markIqCreated = useMarkInstallationIqCreated();

  const installedSensors = useMemo(
    () => (job.sensors || []).filter((sensor) => sensor.installed || sensor.iqDraft),
    [job.sensors],
  );

  const sensorQueries = useQueries({
    queries: installedSensors.map((sensor) => ({
      queryKey: ['installation-iq-context', sensor.mongoSensorId],
      queryFn: () => fetchInstallationIqContext(sensor.mongoSensorId),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const candidates = installedSensors.map<InstalledSensorCandidate>((assignment, index) => ({
    assignment,
    context: sensorQueries[index]?.data ?? null,
    evidence: collectEvidence(job, assignment.mongoSensorId),
    isLoading: sensorQueries[index]?.isLoading ?? false,
    isError: sensorQueries[index]?.isError ?? false,
  }));

  const activeCandidateFromQuery = candidates.find(
    (candidate) => candidate.assignment.mongoSensorId === activeSensorId,
  ) ?? null;
  const activeCandidate = activeCandidateFromQuery
    && activeContext?.sensorId === activeCandidateFromQuery.assignment.mongoSensorId
    ? { ...activeCandidateFromQuery, context: activeContext.context }
    : activeCandidateFromQuery;

  const openCreationDialog = async (candidate: InstalledSensorCandidate, index: number) => {
    const sensorId = candidate.assignment.mongoSensorId;

    setRefreshingSensorId(sensorId);
    try {
      const refreshed = await sensorQueries[index].refetch();
      if (refreshed.isError || !refreshed.data) {
        toast.error('Latest sensor data could not be loaded. Try again.');
        return;
      }
      setActiveContext({ sensorId, context: refreshed.data });
      setActiveSensorId(sensorId);
    } catch {
      toast.error('Latest sensor data could not be loaded. Try again.');
    } finally {
      setRefreshingSensorId(null);
    }
  };

  const handleCreated = async (sensorMongoId: string, iq: LegacyIqRecord) => {
    setSessionCreatedIqBySensorId((current) => ({
      ...current,
      [sensorMongoId]: iq,
    }));

    try {
      await markIqCreated.mutateAsync({
        jobId: job.id,
        sensorId: sensorMongoId,
        createdAt: new Date().toISOString(),
        createdReportId: iq._id,
        createdReportNumber: iq.report_number,
      });
    } catch {
      toast.error('IQ was created, but the job state did not sync. Refresh before creating anything else.');
    }
  };

  if (installedSensors.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Installation Qualification</CardTitle>
          <CardDescription>No installed sensors are available on this job yet.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Installation Qualification
          </CardTitle>
          <CardDescription>
            Create the sensor IQ directly from the approved installation job using the available sensor/transmitter data plus technician checklist evidence.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {candidates.map((candidate, index) => {
            const sessionCreatedIq = sessionCreatedIqBySensorId[candidate.assignment.mongoSensorId];
            const persistedCreatedIq = candidate.assignment.iqDraft?.status === 'created' && candidate.assignment.iqDraft.createdReportId
              ? {
                  _id: candidate.assignment.iqDraft.createdReportId,
                  report_number: candidate.assignment.iqDraft.createdReportNumber || 'IQ',
                }
              : null;
            const existingIqFromSensor = candidate.context?.sensor.installationQualification
              ? {
                  _id: candidate.context.sensor.installationQualification.mongoInstallationQualificationId,
                  report_number: candidate.context.sensor.installationQualification.reportNumber,
                }
              : null;
            const hasSubmittedDraft = candidate.assignment.iqDraft?.status === 'submitted';
            const createdIq = sessionCreatedIq
              || persistedCreatedIq
              || (hasSubmittedDraft ? null : existingIqFromSensor);
            const technicianDraftReady = candidate.assignment.iqDraft?.status === 'submitted' || candidate.assignment.iqDraft?.status === 'created';

            if (candidate.isLoading) {
              return (
                <div key={candidate.assignment.mongoSensorId} className="rounded-lg border p-4 space-y-3">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-16 w-full" />
                </div>
              );
            }

            return (
              <div key={candidate.assignment.mongoSensorId} className="rounded-lg border p-4 space-y-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{candidate.assignment.sensorName}</h3>
                      <Badge variant="outline">{candidate.evidence.length} photo{candidate.evidence.length === 1 ? '' : 's'}</Badge>
                      {candidate.context?.transmitter ? (
                        <Badge variant="success">Transmitter linked</Badge>
                      ) : (
                        <Badge variant="destructive">Missing transmitter link</Badge>
                      )}
                      {candidate.assignment.iqDraft?.status === 'submitted' && (
                        <Badge variant="outline">Technician draft ready</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Thermometer className="h-4 w-4" />
                        {formatReading(candidate)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Waypoints className="h-4 w-4" />
                        {candidate.context?.transmitter?.devId || candidate.context?.sensor.transmitterName || 'No DEV ID'}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <FileImage className="h-4 w-4" />
                        {candidate.context?.sensor.calibrationCertNumber || 'No calibration cert'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Last reading: {formatTimestamp(candidate.context?.sensor.lastReadingAt)}
                    </p>
                    {candidate.assignment.iqDraft?.submittedAt && (
                      <p className="text-sm text-muted-foreground">
                        Technician draft submitted by {candidate.assignment.iqDraft.submittedBy} on {formatTimestamp(candidate.assignment.iqDraft.submittedAt)}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {createdIq ? (
                      <Button
                        variant="outline"
                        onClick={() =>
                          window.open(
                            legacyRoutes.installationQualification(createdIq._id),
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        View {createdIq.report_number}
                      </Button>
                    ) : technicianDraftReady ? (
                      <Button
                        onClick={() => void openCreationDialog(candidate, index)}
                        disabled={
                          !candidate.context?.transmitter
                          || candidate.isError
                          || refreshingSensorId === candidate.assignment.mongoSensorId
                        }
                      >
                        {refreshingSensorId === candidate.assignment.mongoSensorId ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <FilePlus2 className="mr-2 h-4 w-4" />
                        )}
                        {refreshingSensorId === candidate.assignment.mongoSensorId
                          ? 'Refreshing IQ Data'
                          : 'Approve & Create IQ'}
                      </Button>
                    ) : (
                      <Button variant="outline" disabled>
                        Awaiting Technician Draft
                      </Button>
                    )}
                  </div>
                </div>

                {candidate.isError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>IQ context failed to load</AlertTitle>
                    <AlertDescription>
                      Sensor context could not be loaded for this installed sensor. Refresh the page and try again.
                    </AlertDescription>
                  </Alert>
                )}

                {createdIq && (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>IQ created</AlertTitle>
                    <AlertDescription>
                      Report <strong>{createdIq.report_number}</strong> was created from this installation job context.
                    </AlertDescription>
                  </Alert>
                )}

                {!createdIq && !technicianDraftReady && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Technician draft required</AlertTitle>
                    <AlertDescription>
                      The technician must submit the IQ draft from the portal before the operator can approve and create the actual IQ.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <IqCreationDialog
        job={job}
        candidate={activeCandidate}
        linkedQuoteNumber={linkedQuoteNumber}
        open={!!activeSensorId}
        onOpenChange={(open) => {
          if (!open) {
            setActiveSensorId(null);
            setActiveContext(null);
          }
        }}
        onCreated={handleCreated}
      />
    </>
  );
}
